import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on mount
    const token = localStorage.getItem('token');
    const adminToken = localStorage.getItem('adminToken');
    
    // Don't check auth status if we're on admin routes
    if (window.location.pathname.startsWith('/admin')) {
      setLoading(false);
      return;
    }
    
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      checkAuthStatus();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await api.get('/api/user/me');
      if (response.data.success) {
        setUser(response.data.user);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/api/login', { email, password });
      
      if (response.data.success) {
        const { token, userId, isActive, subscriptionStatus } = response.data;
        
        localStorage.setItem('token', token);
        localStorage.setItem('userId', userId);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Set user data based on backend response
        setUser({
          id: userId,
          isActive,
          subscriptionStatus
        });
        
        return { success: true, subscriptionStatus };
      } else {
        return { 
          success: false, 
          error: response.data.error || 'Login failed',
          needsPayment: response.data.needsPayment || false
        };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
        needsPayment: error.response?.data?.needsPayment || false
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/api/register', userData);
      
      if (response.data.success) {
        const { token, userId, needsPayment } = response.data;
        
        localStorage.setItem('token', token);
        localStorage.setItem('userId', userId);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Set basic user data
        setUser({
          id: userId,
          needsPayment
        });
        
        return { success: true, needsPayment };
      } else {
        return {
          success: false,
          error: response.data.error || 'Registration failed'
        };
      }
    } catch (error) {
      console.error('Registration failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  };

  const loginWithToken = (token, userId) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userId', userId);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Set basic user data, will be refreshed later
    setUser({ id: userId });
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    loginWithToken,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {(!loading || window.location.pathname.startsWith('/admin')) && children}
    </AuthContext.Provider>
  );
}; 